import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { FC } from '../../../Services/FeathersClient'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { showToast } from '../../../utils/toast'
import { eConflitto } from './Functions'

const language = cap.language.state || 'IT'
const t = translations[language] || translations.IT

/**
 * D28 — accesso al servizio Feathers `noraConfiguration`.
 *
 * Una lettura (`overview`) e i comandi tipizzati. Ogni comando riuscito o in
 * conflitto ricarica l'overview: la pagina mostra sempre lo stato del server.
 */

const SERVIZIO = 'noraConfiguration'

/**
 * Chiave react-query dell'overview di un environment.
 * @param {number} environmentId - Environment
 * @returns {Array} Query key
 */
export const chiaveOverview = (environmentId) => ['cn', 'overview', environmentId]

/**
 * Hook dell'unica lettura della pagina.
 * @param {number|null} environmentId - Environment scelto (null ⇒ nessuna query)
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export const useOverview = (environmentId) =>
  useQuery({
    queryKey: chiaveOverview(environmentId),
    queryFn: () => FC.service(SERVIZIO).find({ query: { action: 'overview', environmentId } }),
    enabled: !!environmentId
  })

/**
 * Hook per eseguire un comando: un solo `create(payload)`, toast di esito,
 * ricarica dell'overview. Su HTTP 409 non ritenta e non sovrascrive: avvisa e
 * ricarica, così l'utente rivede lo stato corrente prima di salvare di nuovo.
 * L'esito permette al chiamante di chiudere un dialog anche sul conflitto
 * (la card si rimonta con la revisione nuova) e di tenerlo aperto su un errore.
 * @returns {{ esegui: (payload: Object, testoSuccesso?: { titolo: string, dettaglio?: string }) => Promise<'ok'|'conflitto'|'errore'>, inCorso: boolean }}
 */
export const useComando = () => {
  const queryClient = useQueryClient()
  const [inCorso, setInCorso] = useState(false)

  const ricarica = () =>
    queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'cn' })

  const esegui = async (payload, testoSuccesso) => {
    setInCorso(true)
    try {
      await FC.service(SERVIZIO).create(payload)
      showToast(
        'success',
        testoSuccesso?.titolo || t.cnOperazioneRiuscita,
        testoSuccesso?.dettaglio
      )
      await ricarica()
      return 'ok'
    } catch (error) {
      if (eConflitto(error)) {
        showToast('warn', t.cnConflitto, t.cnConflittoDettaglio)
        await ricarica()
        return 'conflitto'
      }
      showToast('error', t.errore, error?.message || t.errGenericError)
      return 'errore'
    } finally {
      setInCorso(false)
    }
  }

  return { esegui, inCorso }
}
